import { TypedDocumentNode, gql } from '@apollo/client';
import { faMedal, faTrophy } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';

import { Aside } from '@sorare/gameplay/src/components/profile/Aside';
import { Block } from '@sorare/gameplay/src/components/profile/Block';
import { ClubLayout } from '@sorare/gameplay/src/components/profile/ClubLayout';
import { CardRewards } from '@sorare/gameplay/src/components/profile/Pro/CardRewards';
import { MonetaryRewards } from '@sorare/gameplay/src/components/profile/Pro/MonetaryRewards';
import { Trophies } from '@sorare/gameplay/src/components/profile/Pro/Trophies';
import { Grid } from '@sorare/gameplay/src/components/profile/ui';

import {
  FootballProHistoryLayoutQuery,
  FootballProHistoryLayoutQueryVariables,
} from './__generated__/layout.graphql';
import { LayoutParams } from './__generated__/routeParams';

const PRO_HISTORY_LAYOUT_QUERY = gql`
  query FootballProHistoryLayoutQuery(
    $slug: String!
    $fiatCurrency: SupportedCurrency!
  ) {
    user(slug: $slug) {
      slug
      so5TrophiesSummary {
        limited: cardRewards(rarity: limited)
        rare: cardRewards(rarity: rare)
        super_rare: cardRewards(rarity: super_rare)
        unique: cardRewards(rarity: unique)
        top1: podiumRankings(ranking: 1)
        top2: podiumRankings(ranking: 2)
        top3: podiumRankings(ranking: 3)
        weiTotalRewards: totalMonetaryRewards(referenceCurrency: WEI) {
          wei
        }
        fiatTotalRewards: totalMonetaryRewards(
          referenceCurrency: $fiatCurrency
        ) {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
    }
  }
  ${monetaryAmountFragment}
` as TypedDocumentNode<
  FootballProHistoryLayoutQuery,
  FootballProHistoryLayoutQueryVariables
>;

const StyledVertical = styled(Vertical)`
  padding: var(--double-unit) 0;
`;

enum TABS {
  STATS = 'stats',
  HISTORY = 'history',
}

const tabsMessages = defineMessages({
  [TABS.STATS]: {
    id: 'MyClubHistory.tabs.stats',
    defaultMessage: 'Stats',
  },
  [TABS.HISTORY]: {
    id: 'MyClubHistory.tabs.history',
    defaultMessage: 'History',
  },
});

const HistoryLayout = () => {
  const { slug } = useTypedParams<LayoutParams>();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { fiatCurrency, isMe } = useCurrentUserContext();
  const [tab, setTab] = useState(TABS.STATS);

  const { data } = useQuery(PRO_HISTORY_LAYOUT_QUERY, {
    variables: {
      slug,
      fiatCurrency: SupportedCurrency[fiatCurrency.code],
    },
  });

  const { user } = data || {};

  if (!user) {
    return null;
  }

  return (
    <ClubLayout home>
      <Vertical gap={0}>
        <>
          {!isLaptopAndAbove && (
            <TabBar value={tab} variant="flat" fullWidth>
              {Object.values(TABS).map(t => (
                <Tab key={t} value={t} onClick={() => setTab(t)}>
                  <FormattedMessage {...tabsMessages[t]} />
                </Tab>
              ))}
            </TabBar>
          )}
          <ConditionalWrapper Wrapper={Grid} wrap={isLaptopAndAbove}>
            <>
              {(isLaptopAndAbove || tab === TABS.HISTORY) && (
                <StyledVertical gap={2}>
                  <Outlet />
                </StyledVertical>
              )}
              {(isLaptopAndAbove || tab === TABS.STATS) && data?.user && (
                <Aside>
                  <Block
                    icon={<FontAwesomeIcon icon={faMedal} />}
                    title={
                      <FormattedMessage
                        id="MyClubHistory.rewards"
                        defaultMessage="Rewards"
                      />
                    }
                  >
                    <LabelM color="var(--c-nd-400)">
                      <FormattedMessage {...glossary.cards} />
                    </LabelM>
                    <CardRewards cardRewards={user.so5TrophiesSummary} />
                    {isMe(slug) && (
                      <>
                        <LabelM color="var(--c-nd-400)">
                          <FormattedMessage
                            id="MyClubHistory.winnings"
                            defaultMessage="Winnings"
                          />
                        </LabelM>
                        <MonetaryRewards
                          wei={
                            user.so5TrophiesSummary.weiTotalRewards?.wei || 0n
                          }
                          fiat={user.so5TrophiesSummary.fiatTotalRewards}
                        />
                      </>
                    )}
                  </Block>
                  <Block
                    icon={<FontAwesomeIcon icon={faTrophy} />}
                    title={
                      <FormattedMessage
                        id="MyClubHistory.trophies"
                        defaultMessage="Trophies"
                      />
                    }
                  >
                    <Trophies trophies={user.so5TrophiesSummary} />
                  </Block>
                </Aside>
              )}
            </>
          </ConditionalWrapper>
        </>
      </Vertical>
    </ClubLayout>
  );
};

export default HistoryLayout;
